import { motion } from "framer-motion";
import { Cpu, Activity, History, ShieldAlert } from "lucide-react";

const FeaturesSection = () => {
  const features = [
    {
      icon: Cpu,
      title: "AI Strategy Engine",
      description:
        "Deep learning models analyse market patterns, social sentiment, and on-chain activity in real time. Plain-English insights, not confusing charts.",
      gridClass: "md:col-span-2 md:row-span-2",
      highlight: true,
    },
    {
      icon: Activity,
      title: "Live Market Simulation",
      description:
        "Paper trade against real-time price feeds from DEXs and major exchanges — same data, zero dollars at stake.",
      gridClass: "md:col-span-1 md:row-span-1",
    },
    {
      icon: History,
      title: "Instant Replay & Backtest",
      description:
        "Rewind the market, replay any meme coin's run, and see exactly how your strategy would have performed.",
      gridClass: "md:col-span-1 md:row-span-1",
    },
    {
      icon: ShieldAlert,
      title: "Rug Pull Detection",
      description:
        "Our AI flags suspicious tokens and contract patterns so you can dodge the scams before they dodge with your money.",
      gridClass: "md:col-span-2 md:row-span-1",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section
      id="features"
      className="relative py-16 md:py-24 overflow-hidden"
      data-testid="features-section"
    >
      {/* Background gradient */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] md:w-[800px] h-[400px] md:h-[800px] bg-[#8B5CF6] rounded-full mix-blend-multiply filter blur-[150px] md:blur-[200px] opacity-10" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-16"
        >
          <span className="inline-block px-3 md:px-4 py-2 rounded-full glass text-xs md:text-sm font-mono text-[#00D4FF] mb-3 md:mb-4">
            WHAT GHOST DOES
          </span>
          <h2 className="font-['Unbounded'] text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-3 md:mb-4">
            The AI That Trades
            <br />
            <span className="gradient-text">Smarter Than You</span>
          </h2>
          <p className="text-[#A1A1AA] text-base md:text-lg max-w-2xl mx-auto px-4">
            GHOST monitors thousands of tokens, identifies high-probability
            opportunities using machine learning, and lets you test strategies
            with zero risk.
          </p>
        </motion.div>

        {/* Features grid - Bento style, responsive */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={itemVariants}
              className={`${feature.gridClass} p-6 md:p-8 rounded-2xl md:rounded-3xl glass ice-hover group cursor-pointer ${
                feature.highlight ? "border-[#00D4FF]/30" : ""
              }`}
              data-testid={`feature-card-${index}`}
            >
              <div
                className={`w-12 h-12 md:w-14 md:h-14 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 ${
                  feature.highlight
                    ? "bg-gradient-to-br from-[#00D4FF] to-[#8B5CF6]"
                    : "bg-[#00D4FF]/10 border border-[#00D4FF]/30"
                } group-hover:shadow-[0_0_30px_rgba(0,212,255,0.4)] transition-all duration-300`}
              >
                <feature.icon
                  size={24}
                  className={feature.highlight ? "text-white" : "text-[#00D4FF]"}
                />
              </div>
              <h3 className="font-['Unbounded'] text-lg md:text-xl lg:text-2xl font-bold mb-2 md:mb-3 text-white group-hover:text-[#00D4FF] transition-colors">
                {feature.title}
              </h3>
              <p className="text-[#A1A1AA] leading-relaxed text-sm md:text-base lg:text-lg">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
